import { Button } from "@repo/ui/components/Base/Button";
import { useMemo, useState } from "react";
import { useUserStore } from "@/stores/userStore";
import { useTenantStore } from "@/stores/tenantStore";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { deepClone } from "@repo/lib/deep";
import {
  type DashboardLayout,
  useDashboardLayout,
} from "@/hooks/useDashboardLayout";
import { KEYS } from "@/lib/constants";
import { DashboardBlock, type DashboardBlockType } from "./DashboardBlocks";
import { UsersRound } from "lucide-react";

const NoTeamDashboardPlaceholder = ({
  canCreateTeam,
  onCreateTeam,
}: {
  canCreateTeam: boolean;
  onCreateTeam: () => void;
}) => (
  <div className="mt-1 rounded-md border border-dashed border-slate-400 bg-slate-50 px-6 py-10 text-center">
    <UsersRound className="mx-auto mb-3 h-8 w-8 text-slate-500" />
    <h2 className="text-xl font-semibold">No teams available</h2>
    <p className="mx-auto mt-2 max-w-xl text-slate-600">
      You do not have access to any teams that can be displayed for this
      organization.
    </p>
    {canCreateTeam && (
      <Button className="mt-4" onClick={onCreateTeam}>
        Create a Team
      </Button>
    )}
  </div>
);

export const TenantDashboard = ({ tenantId }: { tenantId: string }) => {
  const { activeRole, availableTeams, id: userId } = useUserStore((s) => s);
  const navigate = useNavigate();
  const tenantTeams = useTenantStore((s) => s.tenantTeams);

  const handleNavigateCreateTeam = () => {
    navigate(new AppUrlPath(tenantId).teamsPath);
  };

  const hasDisplayableTeams = availableTeams.length > 0;
  const canCreateTeam = activeRole === "admin";

  const defaultDashboardLayout: DashboardLayout = {
    layout: useMemo(
      () => [
        // 'all your items' block
        {
          id: "youritems",
          type: "youritems",
          visible: true,
        },

        // team blocks
        ...availableTeams.map(
          (team) =>
            ({
              id: "team:" + team.id,
              type: "team",
              team: team.id,
              visible: true,
            }) satisfies DashboardBlockType
        ),
      ],
      [availableTeams]
    ),
  };

  const { dashboardLayout, saveDashboardLayout } = useDashboardLayout(
    KEYS.DASHBOARD_LAYOUT,
    tenantId,
    defaultDashboardLayout
  );

  const [customizing, setCustomizing] = useState(false);
  const [customizedLayout, setCustomizedLayout] = useState(dashboardLayout);

  const layout = customizing ? customizedLayout : dashboardLayout;

  return (
    <div className="flex flex-col flex-grow gap-4">
      <div className="flex flex-row justify-between">
        <h1 className="mt-5 text-2xl">Teams</h1>
        {!customizing && hasDisplayableTeams && (
          <Button
            onClick={() => {
              setCustomizing(true);
              setCustomizedLayout(deepClone(dashboardLayout));
            }}
          >
            Customize Dashboard
          </Button>
        )}
        {customizing && (
          <div className="flex flex-row gap-4">
            <Button variant="destructive" onClick={() => setCustomizing(false)}>
              Cancel Changes
            </Button>
            <Button
              onClick={() => {
                setCustomizing(false);
                saveDashboardLayout(customizedLayout);
              }}
            >
              Save Dashboard
            </Button>
          </div>
        )}
      </div>

      {hasDisplayableTeams ? (
        <div className="mt-1 grid gap-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {layout.layout.map(
            (block, index) =>
              (block.visible || customizing) && (
                <DashboardBlock
                  key={block.id}
                  tenantId={tenantId}
                  blockType={block}
                  index={index}
                  count={layout.layout.length}
                  editing={customizing}
                  onVisibilityChange={(index, visible) => {
                    // change visibility of this block
                    const newLayout = deepClone(customizedLayout);
                    newLayout.layout[index].visible = visible;
                    setCustomizedLayout(newLayout);
                  }}
                  onMoveTo={(newIndex) => {
                    // move this block to different array position
                    const newLayout = deepClone(customizedLayout);
                    newLayout.layout.splice(newIndex, 0, deepClone(block));
                    const prevIndex = index + (newIndex < index ? 1 : 0);
                    newLayout.layout.splice(prevIndex, 1);
                    setCustomizedLayout(newLayout);
                  }}
                />
              )
          )}
        </div>
      ) : (
        <NoTeamDashboardPlaceholder
          canCreateTeam={canCreateTeam}
          onCreateTeam={handleNavigateCreateTeam}
        />
      )}

      {hasDisplayableTeams && (
        <div>
          <p className="text-center mb-1">
            <span className="inline-block px-2 py-1 rounded-md bg-primary/20 mr-1">
              Your Active Requests:
            </span>{" "}
            Requests you submitted to this team for which no decision has been
            made
          </p>
          {tenantTeams.find((team) => team.allUsers.includes(userId)) && (
            <p className="text-center mb-1">
              <span className="inline-block px-2 py-1 rounded-md bg-secondary/20 mr-1">
                Pending Requests:
              </span>{" "}
              Requests in this team that have not been added to an agenda yet
            </p>
          )}
        </div>
      )}
    </div>
  );
};
